// Basic Level Quiz Component for Landing Page
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import './BasicLevelQuiz.css';

const BasicLevelQuiz = () => {
  const { translate } = useLanguage();
  const [quizData, setQuizData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(null);
  const [videoRef, setVideoRef] = useState(null);
  const [segmentNotification, setSegmentNotification] = useState('');

  // Load quiz data
  useEffect(() => {
    const loadQuiz = async () => {
      setLoading(true);
      try {
        const response = await fetch('https://portal.profezac.com/api/quizzes/demo/basic/basketball');
        const data = await response.json();
        setQuizData(data);
        setUserAnswers(new Array(data.questions.length).fill(null));
      } catch (error) {
        console.error('Error loading quiz:', error);
        // Fallback quiz data in case API is unavailable
        const fallbackQuiz = {
          title: "Demo Quiz: Basketball (Basic Level)",
          description: "Try our basic level quiz system",
          questions: [
            {
              questionType: "wordOrder",
              questionText: "Arrange these words in the correct order:",
              originalText: "This is a basketball.",
              options: ["basketball.", "is", "soccer", "This", "a", "run", "jump"],
              correctAnswer: ["This", "is", "a", "basketball."],
              explanation: "Correct order: This → is → a → basketball.",
              timestamp: 7.84,
              phraseEndTime: 10.02
            },
            {
              questionType: "wordOrder", 
              questionText: "Arrange these words in the correct order:",
              originalText: "A basketball court.",
              options: ["court.", "run", "soccer", "A", "basketball", "jump"],
              correctAnswer: ["A", "basketball", "court."],
              explanation: "Correct order: A → basketball → court.",
              timestamp: 15.22,
              phraseEndTime: 17.16
            }
          ]
        };
        setQuizData(fallbackQuiz);
        setUserAnswers(new Array(fallbackQuiz.questions.length).fill(null));
      }
      setLoading(false);
    };

    loadQuiz();
  }, []);

  const startQuiz = () => {
    setQuizStarted(true);
    setCurrentQuestionIndex(0);
    setQuizFinished(false);
    setScore(0);
    setUserAnswers(new Array(quizData.questions.length).fill(null));
  };

  const addWordToAnswer = (word) => {
    const newAnswers = [...userAnswers];
    if (!newAnswers[currentQuestionIndex]) {
      newAnswers[currentQuestionIndex] = [];
    }
    newAnswers[currentQuestionIndex].push(word);
    setUserAnswers(newAnswers);
  };

  const removeWordFromAnswer = (wordIndex) => {
    const newAnswers = [...userAnswers];
    if (newAnswers[currentQuestionIndex]) {
      newAnswers[currentQuestionIndex].splice(wordIndex, 1);
      setUserAnswers(newAnswers);
    }
  };

  const resetQuestion = () => {
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = [];
    setUserAnswers(newAnswers);
    setShowResult(null);
  };

  const checkAnswer = () => {
    const currentAnswer = userAnswers[currentQuestionIndex] || [];
    const correctAnswer = quizData.questions[currentQuestionIndex].correctAnswer;
    const isCorrect = JSON.stringify(currentAnswer) === JSON.stringify(correctAnswer);
    setShowResult(isCorrect);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < quizData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowResult(null);
    }
  };


  const finishQuiz = () => {
    let finalScore = 0;
    userAnswers.forEach((answer, index) => {
      if (answer && answer.length > 0) {
        const correctAnswer = quizData.questions[index].correctAnswer;
        if (JSON.stringify(answer) === JSON.stringify(correctAnswer)) {
          finalScore++;
        }
      }
    });
    setScore(finalScore);
    setQuizFinished(true);
  };

  const restartQuiz = () => {
    setQuizStarted(false);
    setQuizFinished(false);
    setCurrentQuestionIndex(0);
    setScore(0);
    setUserAnswers(new Array(quizData.questions.length).fill(null));
    setShowResult(null);
  };

  const jumpToVideoSegment = (startTime, endTime) => {
    if (videoRef && startTime) {
      // Scroll to video first
      videoRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Set video time to the segment timestamp
      videoRef.currentTime = startTime;
      
      // Play the video
      videoRef.play().catch(e => {
        console.log('Video autoplay prevented by browser policy');
      });
      
      // If endTime is provided, pause the video when it reaches that time
      if (endTime && endTime > startTime) {
        const checkTime = () => {
          if (videoRef.currentTime >= endTime) {
            videoRef.pause();
            videoRef.removeEventListener('timeupdate', checkTime);
          }
        };
        videoRef.addEventListener('timeupdate', checkTime);
      }
      
      // Show notification
      showSegmentNotification(startTime, endTime);
    }
  };

  const showSegmentNotification = (startTime, endTime) => {
    const message = endTime ? 
      `Reproduciendo segmento` :
      `Saltó al segmento`;
    
    setSegmentNotification(message);
    
    // Clear notification after 3 seconds
    setTimeout(() => {
      setSegmentNotification('');
    }, 3000);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando quiz demo...</span>
        </div>
        <p className="mt-3">Cargando quiz demo...</p>
      </div>
    );
  }

  if (!quizData) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">⚠️ No se pudo cargar el quiz demo. Por favor, intenta más tarde.</p>
      </div>
    );
  }

  if (!quizStarted) {
    return (
      <div className="basic-level-quiz">
        {/* Enhanced Quiz Start Section */}
        <div className="text-center mb-4">
          <div className="card shadow-sm border-0 p-4 mb-4" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: '15px'
          }}>
            <div className="mb-3">
              <div style={{ fontSize: '3rem' }}>🎯</div>
            </div>
            <h4 className="fw-bold mb-3">{quizData.title}</h4>
            <p className="mb-4" style={{ fontSize: '1.1rem', opacity: 0.95 }}>
              Quiz completo real del <strong>nivel básico</strong> con frases simples y apoyo visual
            </p>
            <div className="row g-3 mb-4">
              <div className="col-sm-4">
                <div className="bg-white bg-opacity-20 rounded p-2" style={{ color: '#333' }}>
                  <div>📱 <strong>Interactivo</strong></div>
                  <small>Video con segmentos</small>
                </div>
              </div>
              <div className="col-sm-4">
                <div className="bg-white bg-opacity-20 rounded p-2" style={{ color: '#333' }}>
                  <div>⏱️ <strong>{quizData.questions.length} Preguntas</strong></div>
                  <small>Nivel principiante</small>
                </div>
              </div>
              <div className="col-sm-4">
                <div className="bg-white bg-opacity-20 rounded p-2" style={{ color: '#333' }}>
                  <div>🏆 <strong>Resultados</strong></div>
                  <small>Al finalizar</small>
                </div>
              </div>
            </div>
            <button 
              className="btn btn-success btn-lg px-5 py-3 fw-bold shadow" 
              onClick={startQuiz}
              style={{
                fontSize: '1.2rem',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(45deg, #28a745, #20c997)',
                color: 'white'
              }}
            >
              🚀 Prueba un Cuestionario del Curso
            </button>
            <div className="mt-2">
              <small className="text-light" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                (100% gratuita y sin compromiso)
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (quizFinished) {
    const percentage = Math.round((score / quizData.questions.length) * 100);
    let message = '';
    let color = '';
    
    if (percentage >= 80) {
      message = '🎉 ¡Excelente trabajo!';
      color = 'success';
    } else if (percentage >= 60) {
      message = '👍 ¡Bien hecho!';
      color = 'warning';
    } else {
      message = '💪 ¡Sigue practicando!';
      color = 'danger';
    }

    return (
      <div className="basic-level-quiz">
        <div className="quiz-results text-center">
          <h4 className={`text-${color} mb-4`}>{message}</h4>
          <div className="score-display mb-4">
            <div className={`display-4 fw-bold text-${color}`}>
              {score}/{quizData.questions.length}
            </div>
            <div className={`fs-3 text-${color}`}>
              {percentage}% Correcto
            </div>
          </div>
          
          <div className="answers-summary mb-4">
            <h5>📊 Resumen de Respuestas:</h5>
            <div className="answers-list">
              {userAnswers.map((answer, index) => {
                const question = quizData.questions[index];
                const isCorrect = answer && JSON.stringify(answer) === JSON.stringify(question.correctAnswer);
                return (
                  <div key={index} className={`answer-item ${isCorrect ? 'correct' : 'incorrect'}`}>
                    <strong>Pregunta {index + 1}:</strong> "{question.originalText}"<br/>
                    <span>
                      {isCorrect ? '✅' : '❌'} 
                      {answer && answer.length > 0 ? answer.join(' ') : 'Sin respuesta'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="quiz-completion-message mb-4">
            <p><strong>🚀 ¡Has probado el nivel básico!</strong></p>
            <p>El nivel básico tiene <strong>cientos de videos</strong> como este, con apoyo visual y frases simples para principiantes.</p>
            <p>Construye tu base de inglés desde cero con nuestro método visual y progresivo.</p>
          </div>
          
          <div className="quiz-actions">
            <button className="btn btn-outline-secondary me-3" onClick={restartQuiz}>
              🔄 Intentar de Nuevo
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => window.location.href = 'https://portal.profezac.com/subscription/basic'}
            >
              🎯 Ver Más Contenido
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = quizData.questions[currentQuestionIndex];
  const currentAnswer = userAnswers[currentQuestionIndex] || [];
  const progress = ((currentQuestionIndex + 1) / quizData.questions.length) * 100;
  // Create available words by removing used instances (allow multiple instances of same word)
  const availableWords = [];
  const usedCount = {};
  
  // Count how many times each word is used in current answer
  currentAnswer.forEach(word => {
    usedCount[word] = (usedCount[word] || 0) + 1;
  });
  
  // Add words to available list, respecting usage counts
  currentQuestion.options.forEach(word => {
    const timesUsed = usedCount[word] || 0;
    const totalAvailable = currentQuestion.options.filter(w => w === word).length;
    if (timesUsed < totalAvailable) {
      availableWords.push(word);
    }
  });

  return (
    <div className="basic-level-quiz">
      {/* Video Section - Click to play/pause like other videos */}
      <div className="video-section mb-4" style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ 
          width: '300px', 
          height: '375px', 
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '8px',
          boxShadow: '0 15px 30px rgba(0,0,0,0.15)'
        }}>
          <video
            ref={setVideoRef}
            controls
            poster="/videos/basketball-basic copy.png"
            onClick={(e) => {
              if (e.target.paused) {
                e.target.play();
              } else {
                e.target.pause();
              }
            }}
            style={{ 
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center bottom',
              display: 'block',
              cursor: 'pointer'
            }}
            controlsList="nodownload nofullscreen"
            preload="metadata"
            playsInline
          >
            <source src="/videos/basic_nouns_basketball.mp4" type="video/mp4" />
            Tu navegador no soporta el elemento de video.
          </video>
        </div>
      </div>

      <div className="quiz-question">
        {/* Question progress - optimized spacing */}
        <div className="question-progress mb-3">
          <div className="progress">
            <div className="progress-bar bg-primary" style={{ width: `${progress}%` }}></div>
          </div>
          <small className="text-muted">
            Pregunta {currentQuestionIndex + 1} de {quizData.questions.length}
          </small>
        </div>

        {/* Ver Segmento button - positioned above instruction text */}
        {currentQuestion.timestamp && (
          <div className="d-flex justify-content-center mb-3">
            <button 
              className="btn btn-info btn-sm" 
              onClick={() => jumpToVideoSegment(currentQuestion.timestamp, currentQuestion.phraseEndTime || currentQuestion.timestamp + 4.5)}
              style={{ minWidth: '140px' }}
            >
              {segmentNotification ? (
                <small>{segmentNotification}</small>
              ) : (
                <>🎥 Ver Segmento</>
              )}
            </button>
          </div>
        )}
        
        <p className="quiz-instructions mb-4">
          {translate('quiz_instruction_distractor') || 'Find the correct words and arrange them in the correct order to match what you heard in the video:'}
        </p>
        
        <div className="word-bank mb-4">
          <h6>Palabras disponibles:</h6>
          <div className="words-container">
            {availableWords.map((word, index) => (
              <button
                key={index}
                className="btn btn-light btn-sm word-option"
                onClick={(event) => {
                  addWordToAnswer(word);
                  event.target.blur();
                }}
              >
                {word}
              </button>
            ))}
          </div>
        </div>
        
        <div className="answer-area mb-4">
          <h6>Tu respuesta:</h6>
          <div className="answer-container">
            {currentAnswer.length === 0 ? (
              <span className="text-muted">Haz clic en las palabras para formar tu respuesta...</span>
            ) : (
              currentAnswer.map((word, index) => (
                <button
                  key={index}
                  className="btn btn-success btn-sm answer-word"
                  onClick={() => removeWordFromAnswer(index)}
                >
                  {word} ✕
                </button>
              ))
            )}
          </div>
        </div>


        <div className="quiz-controls mb-4">
          <button 
            className="btn btn-primary me-2" 
            onClick={checkAnswer}
            disabled={currentAnswer.length === 0}
          >
            Verificar Respuesta
          </button>
          {currentQuestionIndex === quizData.questions.length - 1 && (
            <button className="btn btn-success" onClick={finishQuiz}>
              🏁 Finalizar Quiz
            </button>
          )}
        </div>

        {showResult !== null && (
          <div className={`quiz-result alert alert-${showResult ? 'success' : 'danger'}`}>
            {showResult ? (
              <>
                <h6>✅ ¡Correcto!</h6>
                <p className="mb-0">Has ordenado las palabras perfectamente.</p>
                {currentQuestion.explanation && (
                  <small className="text-muted d-block mt-2">{currentQuestion.explanation}</small>
                )}
              </>
            ) : (
              <>
                <h6>❌ No es correcto</h6>
                <p className="mb-0">
                  La respuesta correcta es: <strong>{currentQuestion.correctAnswer.join(' ')}</strong>
                </p>
                {currentQuestion.explanation && (
                  <small className="text-muted d-block mt-2">{currentQuestion.explanation}</small>
                )}
              </>
            )}
            
            {/* Next Question button in the result area */}
            {currentQuestionIndex < quizData.questions.length - 1 && (
              <div className="mt-3">
                <button className="btn btn-outline-primary" onClick={nextQuestion}>
                  Siguiente Pregunta →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BasicLevelQuiz;